import {
    EuiButton,
    EuiCheckbox,
    EuiComboBox,
    EuiFieldNumber,
    EuiFlexGrid,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFlyout,
    EuiFlyoutBody,
    EuiFlyoutFooter,
    EuiFlyoutHeader,
    EuiForm,
    EuiFormRow,
    EuiIcon,
    EuiLoadingSpinner,
    EuiOverlayMask,
    EuiSpacer,
    EuiTextArea,
    EuiTitle,
    EuiToolTip,
} from "@elastic/eui";
import React, { useEffect, useState } from "react";

import {
    useResumeContentWorkflowMutation,
    useStartContentWorkflowMutation,
} from "../../api/react-query/mutations/content-generator";
import {
    useGetWorkflowCurrentStepQuery,
    useGetWorkflowValidatedDataQuery,
} from "../../api/react-query/queries/content-generator";
import { ContentGenerationStatus, Flavor, GeneratedContent, Tag } from "../../utils/types";
import {
    mapFlavorsToOptions,
    mapFlavorsToSelectedOptions,
    mapGeneratedEffectsToSelectedOptions,
    mapSelectedOptionsToEffects,
    mapTagsToOptions,
} from "./productContentGenerationUtils";

interface GenerateProductContentProps {
    product_name: string;
    product_id: string;
    flavors: Flavor[] | undefined;
    enableFlavorsQuery: (arg0: boolean) => void;
    tags: Tag[] | undefined;
    enableTagsQuery: (arg0: boolean) => void;
}

export const GenerateProductContent = ({
    product_name,
    product_id,
    flavors,
    enableFlavorsQuery,
    tags,
    enableTagsQuery,
}: GenerateProductContentProps) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [workflowId, setWorkflowId] = useState<string | null>(null);
    const [workflowStatus, setWorkflowStatus] = useState<ContentGenerationStatus | null>(null);
    const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [braveMode, setBraveMode] = useState(true);
    const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);

    const closeFlyout = () => setIsFlyoutVisible(false);
    const showFlyout = () => {
        enableFlavorsQuery(true);
        enableTagsQuery(true);
        setIsFlyoutVisible(true);
    };

    const isWorkflowStarted = Boolean(workflowId);
    const isValidationStep = isWorkflowStarted && currentStep === 8;

    const startMutation = useStartContentWorkflowMutation();

    const startContentGeneration = () => {
        setIsGenerating(true);
        startMutation.mutate(
            { product_name, product_id, brave_mode: braveMode },
            {
                onSuccess: (data) => {
                    setWorkflowId(data.process_id);
                },
                onError: (error) => {
                    console.error("Error starting content generation workflow:", error);
                    setIsGenerating(false);
                },
            }
        );
    };

    const { data: currentStepData } = useGetWorkflowCurrentStepQuery(
        workflowId ?? undefined,
        isWorkflowStarted && isGenerating,
        4000
    );

    const { data: validatedData } = useGetWorkflowValidatedDataQuery(workflowId ?? undefined, isValidationStep);

    const resumeMutation = useResumeContentWorkflowMutation();

    const validateWorkflowContent = () => {
        if (!workflowId || !generatedContent) return;

        setIsGenerating(true);
        resumeMutation.mutate(
            { workflowId, validation_data: { ...generatedContent } },
            {
                onSuccess: () => {
                    setGeneratedContent(null);
                    closeFlyout();
                },
                onError: (error) => {
                    console.error("Error validating content generation workflow:", error);
                    setWorkflowStatus(ContentGenerationStatus.FAILED);
                    setIsGenerating(false);
                    closeFlyout();
                },
            }
        );
    };

    const generatedContentSetter = (key: keyof GeneratedContent, value: any) => {
        if (!generatedContent) return;
        setGeneratedContent({
            ...generatedContent,
            [key]: value,
        });
    };

    useEffect(() => {
        if (!currentStepData) return;

        setCurrentStep(currentStepData.current_step);

        const workflowState = currentStepData.workflow_state;

        if (workflowState === ContentGenerationStatus.COMPLETED) {
            window.location.reload();
            return;
        }

        if (workflowState) {
            setWorkflowStatus(workflowState);
        }

        if (workflowState === ContentGenerationStatus.AWAITING_VALIDATION) {
            if (!validatedData) {
                setWorkflowStatus(ContentGenerationStatus.FAILED);
                setIsGenerating(false);
            }

            setGeneratedContent(validatedData);
            setWorkflowStatus(ContentGenerationStatus.AWAITING_VALIDATION);
            setIsGenerating(false);
        }

        if (
            currentStepData.current_step === currentStepData.total_steps ||
            workflowState === ContentGenerationStatus.FAILED
        ) {
            setIsGenerating(false);
        }
    }, [currentStepData, validatedData]);

    return (
        <div>
            <EuiFlexGroup alignItems="center" gutterSize="m">
                <EuiFlexItem grow={false}>
                    <EuiFlexGroup alignItems="center" gutterSize="xs" responsive={false}>
                        <EuiFlexItem grow={false}>
                            <EuiCheckbox
                                id="brave-mode-checkbox"
                                label="Brave Mode"
                                checked={braveMode}
                                onChange={(e) => setBraveMode(e.target.checked)}
                                disabled={isGenerating}
                            />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiToolTip content="By enabling Brave mode you will skip the AI validation step and trust it to output correct content.">
                                <EuiIcon type="iInCircle" />
                            </EuiToolTip>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <GenerateContentButton
                        isGenerating={isGenerating}
                        workflowStatus={workflowStatus}
                        currentStep={currentStep}
                        startContentGeneration={startContentGeneration}
                        showFlyout={showFlyout}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>

            {isFlyoutVisible && generatedContent && (
                <EuiOverlayMask>
                    <EuiFlyout onClose={closeFlyout} size="m" paddingSize="l" ownFocus>
                        <EuiFlyoutHeader>
                            <EuiTitle size="m">
                                <h2>Validate AI Content</h2>
                            </EuiTitle>
                        </EuiFlyoutHeader>

                        <EuiFlyoutBody>
                            The following content has been generated
                            <EuiSpacer />
                            <EuiForm style={{ fontSize: "10px", padding: "5px" }}>
                                <EuiFlexGroup justifyContent="spaceBetween" alignItems="center">
                                    <EuiFlexItem grow={false} style={{ minWidth: "48%" }}>
                                        <EuiFormRow label="Long Description (English)">
                                            <EuiTextArea
                                                fullWidth
                                                defaultValue={generatedContent.description_en}
                                                onChange={(e) =>
                                                    generatedContentSetter("description_en", e.target.value)
                                                }
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false} style={{ minWidth: "48%" }}>
                                        <EuiFormRow label="Short Description (English)">
                                            <EuiTextArea
                                                fullWidth
                                                defaultValue={generatedContent.short_description_en}
                                                onChange={(e) =>
                                                    generatedContentSetter("short_description_en", e.target.value)
                                                }
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                                <EuiSpacer />
                                <EuiFlexGroup
                                    justifyContent="spaceBetween"
                                    alignItems="center"
                                    style={{ fontSize: "10px" }}
                                >
                                    <EuiFlexItem grow={false} style={{ minWidth: "48%" }}>
                                        <EuiFormRow label="Long Description (Dutch)">
                                            <EuiTextArea
                                                fullWidth
                                                defaultValue={generatedContent.description_nl}
                                                onChange={(e) =>
                                                    generatedContentSetter("description_nl", e.target.value)
                                                }
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false} style={{ minWidth: "48%" }}>
                                        <EuiFormRow label="Short Description (Dutch)">
                                            <EuiTextArea
                                                fullWidth
                                                defaultValue={generatedContent.short_description_nl}
                                                onChange={(e) =>
                                                    generatedContentSetter("short_description_nl", e.target.value)
                                                }
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                                <EuiSpacer />
                                <EuiFlexGroup direction="column" justifyContent="spaceBetween" gutterSize="l">
                                    <EuiFlexItem grow={false}>
                                        <EuiFormRow label="Flavors" fullWidth>
                                            <EuiComboBox
                                                fullWidth
                                                placeholder="Select flavors"
                                                isLoading={!flavors}
                                                options={mapFlavorsToOptions(flavors)}
                                                selectedOptions={mapFlavorsToSelectedOptions(flavors)}
                                                onChange={(selectedOptions) =>
                                                    generatedContentSetter(
                                                        "flavors",
                                                        selectedOptions.map((option) => option.label)
                                                    )
                                                }
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiFormRow label="Effects" fullWidth>
                                            <EuiComboBox
                                                placeholder="Select effects"
                                                fullWidth
                                                isLoading={!tags}
                                                options={mapTagsToOptions(tags)}
                                                selectedOptions={mapGeneratedEffectsToSelectedOptions(
                                                    generatedContent.effects
                                                )}
                                                onChange={(selectedOptions) => {
                                                    const newEffects = mapSelectedOptionsToEffects(
                                                        selectedOptions,
                                                        generatedContent.effects
                                                    );
                                                    setGeneratedContent({
                                                        ...generatedContent,
                                                        effects: newEffects,
                                                    });
                                                }}
                                            />
                                        </EuiFormRow>
                                    </EuiFlexItem>
                                    <EuiFlexItem grow={false}>
                                        <EuiFlexGrid columns={2} style={{ gap: "10px" }}>
                                            {generatedContent.effects.map((effect, index) => (
                                                <EuiFormRow
                                                    fullWidth
                                                    style={{ minWidth: "48%", marginTop: "16px" }}
                                                    key={index}
                                                    label={effect.effect_name}
                                                    helpText="Set intensity (1-100)"
                                                >
                                                    <EuiFieldNumber
                                                        fullWidth
                                                        style={{ minWidth: "48%" }}
                                                        min={0}
                                                        max={100}
                                                        value={effect.intensity}
                                                        onChange={(e) => {
                                                            const newEffects = [...generatedContent.effects];
                                                            newEffects[index] = {
                                                                ...effect,
                                                                intensity: Number(e.target.value),
                                                            };
                                                            generatedContentSetter("effects", newEffects);
                                                        }}
                                                    />
                                                </EuiFormRow>
                                            ))}
                                        </EuiFlexGrid>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            </EuiForm>
                        </EuiFlyoutBody>

                        <EuiFlyoutFooter>
                            <EuiButton onClick={validateWorkflowContent} fill>
                                Validate Content
                            </EuiButton>
                        </EuiFlyoutFooter>
                    </EuiFlyout>
                </EuiOverlayMask>
            )}
        </div>
    );
};

interface GenerateContentButtonProps {
    isGenerating: boolean;
    workflowStatus: string | null;
    currentStep: number;
    startContentGeneration: () => void;
    showFlyout: () => void;
}

const GenerateContentButton: React.FC<GenerateContentButtonProps> = ({
    isGenerating,
    workflowStatus,
    currentStep,
    startContentGeneration,
    showFlyout,
}) => {
    return (
        <EuiButton
            fill
            minWidth={200}
            onClick={
                workflowStatus === ContentGenerationStatus.AWAITING_VALIDATION ? showFlyout : startContentGeneration
            }
            disabled={isGenerating}
        >
            {isGenerating ? (
                <>
                    <EuiLoadingSpinner />
                    <p>Step: {currentStep}</p>
                </>
            ) : (
                <>
                    {workflowStatus === ContentGenerationStatus.FAILED && `Workflow failed at step ${currentStep}`}
                    {workflowStatus === ContentGenerationStatus.AWAITING_VALIDATION && <p>Validate AI Content</p>}
                    {workflowStatus !== ContentGenerationStatus.AWAITING_VALIDATION &&
                        workflowStatus !== ContentGenerationStatus.FAILED &&
                        `Generate Product Content`}
                </>
            )}
        </EuiButton>
    );
};
